import { useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { getWork, WORK, PROFILE } from "../data/work";
import { Reveal } from "../components/Reveal";
import BrandMark from "../components/BrandMark";
import Footer from "../components/Footer";

const Meta = ({ label, value }) => (
  <div className="border-t border-line py-4">
    <p className="text-xs uppercase tracking-[0.2em] text-muted2 mb-1">{label}</p>
    <p className="text-ink text-base">{value}</p>
  </div>
);

export default function CaseStudy() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const item = getWork(slug);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 120]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
  }, [slug]);

  if (!item) {
    return (
      <main className="min-h-screen flex flex-col items-center justify-center gap-6 px-6">
        <h1 className="font-serif text-4xl text-ink">Case not found.</h1>
        <Link to="/" className="link-underline text-vermilion">← Back home</Link>
      </main>
    );
  }

  const idx = WORK.findIndex((w) => w.slug === slug);
  const next = WORK[(idx + 1) % WORK.length];

  return (
    <main data-testid={`case-page-${slug}`} className="overflow-hidden">
      {/* Header */}
      <section ref={ref} className="relative px-5 md:px-12 pt-28 md:pt-36 pb-12 max-w-[1600px] mx-auto">
        <Reveal>
          <Link
            to="/"
            data-testid="case-back"
            className="link-underline inline-flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-muted2 mb-10"
          >
            ← All Work
          </Link>
        </Reveal>
        <Reveal delay={0.05}>
          <p className="text-sm uppercase tracking-[0.25em] text-vermilion mb-5">
            {item.client ? `${item.client}, ${item.year}` : item.year}
          </p>
          <h1 className="font-serif font-semibold text-ink leading-[0.92] tracking-[-0.02em] text-[12vw] md:text-[7vw]">
            {item.title}
          </h1>
          <p className="mt-6 font-serif italic text-2xl md:text-4xl text-ink/80 max-w-3xl">
            “{item.tagline}”
          </p>
        </Reveal>
      </section>

      {/* Cover */}
      <section className="px-5 md:px-12 max-w-[1600px] mx-auto">
        <div className="relative overflow-hidden aspect-[16/9] bg-ink/5">
          <motion.div style={{ y }} className="w-full h-[120%]">
            <BrandMark slug={slug} size="hero" />
          </motion.div>
        </div>
      </section>

      {/* Body */}
      <section className="px-5 md:px-12 py-20 md:py-28 max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
        {/* metadata */}
        <aside className="lg:col-span-4 lg:sticky lg:top-28 self-start">
          <Meta label="Discipline" value={item.discipline} />
          {item.client && <Meta label="Client" value={item.client} />}
          <Meta label="Year" value={item.year} />
          <Meta label="Outcome" value={item.result} />
        </aside>

        {/* story */}
        <div className="lg:col-span-8 space-y-14">
          {item.brief && (
            <Reveal>
              <h2 className="text-xs uppercase tracking-[0.25em] text-vermilion mb-3">The Brief</h2>
              <p className="font-serif text-2xl md:text-3xl text-ink leading-snug">{item.brief}</p>
            </Reveal>
          )}
          <Reveal>
            <h2 className="text-xs uppercase tracking-[0.25em] text-vermilion mb-3">The Insight</h2>
            <p className="text-lg md:text-xl text-muted2 leading-relaxed">{item.insight}</p>
          </Reveal>
          {item.idea && (
            <Reveal>
              <h2 className="text-xs uppercase tracking-[0.25em] text-vermilion mb-3">The Big Idea</h2>
              <p className="font-serif text-2xl md:text-3xl text-ink leading-snug">{item.idea}</p>
            </Reveal>
          )}
          {item.approach?.length > 0 && (
            <Reveal>
              <h2 className="text-xs uppercase tracking-[0.25em] text-vermilion mb-4">The Approach</h2>
              <ul className="space-y-4">
                {item.approach.map((a, i) => (
                  <li key={i} className="flex gap-4 text-lg text-ink border-b border-line pb-4">
                    <span className="font-serif text-vermilion">0{i + 1}</span>
                    <span className="text-muted2">{a}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
          )}
          {item.link && (
            <Reveal>
              <a
                data-testid="case-external-link"
                href={item.link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group inline-flex items-center gap-3 bg-ink text-paper px-7 py-4 text-sm uppercase tracking-[0.2em] hover:bg-vermilion transition-colors duration-300"
              >
                {item.link.label}
                <span className="inline-block transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5">↗</span>
              </a>
            </Reveal>
          )}
          {item.links?.length > 0 && (
            <Reveal>
              <div className="flex flex-col sm:flex-row flex-wrap gap-4">
                {item.links.map((l, i) => (
                  <a
                    key={l.url}
                    data-testid={`case-link-${i}`}
                    href={l.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex items-center gap-3 bg-ink text-paper px-7 py-4 text-sm uppercase tracking-[0.2em] hover:bg-vermilion transition-colors duration-300"
                  >
                    {l.label}
                    <span className="inline-block transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-0.5">↗</span>
                  </a>
                ))}
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* Copy lines */}
      {item.lines?.length > 0 && (
        <section className="bg-ink text-paper px-5 md:px-12 py-24 md:py-32">
          <div className="max-w-[1600px] mx-auto">
            <p className="text-xs uppercase tracking-[0.25em] text-vermilion mb-12">
              Selected Lines
            </p>
            <div className="space-y-10 md:space-y-14">
              {item.lines.map((l, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <p className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.01em] max-w-6xl">
                    <span className="text-paper/40 mr-4 text-2xl align-top">0{i + 1}</span>
                    {l}
                  </p>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Brand architecture (UNCOLOGNE) */}
      {item.architecture && (
        <section
          data-testid="case-architecture"
          className="bg-ink text-paper px-5 md:px-12 py-24 md:py-32"
        >
          <div className="max-w-[1600px] mx-auto space-y-20 md:space-y-28">
            {item.architecture.ideaBody && (
              <Reveal>
                <p className="text-xs uppercase tracking-[0.25em] text-vermilion mb-6">
                  {item.architecture.ideaHeading}
                </p>
                <p className="font-serif text-3xl md:text-5xl leading-[1.1] max-w-5xl">
                  {item.architecture.ideaBody}
                </p>
              </Reveal>
            )}

            {/* Collections */}
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-vermilion mb-10">
                The Collection
              </p>
              <div className="border-t border-paper/20">
                {item.architecture.collections.map((c, i) => (
                  <Reveal
                    key={c.no}
                    delay={i * 0.04}
                    className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-8 py-8 md:py-10 border-b border-paper/20"
                  >
                    <div className="md:col-span-3 flex items-baseline gap-4">
                      <span className="font-serif text-4xl md:text-5xl text-vermilion">{c.no}</span>
                      <span className="font-serif text-2xl md:text-3xl">{c.name}</span>
                    </div>
                    <p className="md:col-span-4 font-serif italic text-xl md:text-2xl text-paper/90">
                      {c.idea}
                    </p>
                    <div className="md:col-span-5 space-y-2 text-paper/70">
                      <p>{c.note}</p>
                      <p className="text-xs uppercase tracking-[0.2em] text-paper/50">{c.behaviour}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>

            {/* Material discipline */}
            <Reveal className="grid grid-cols-1 md:grid-cols-2 gap-12">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-vermilion mb-6">We prioritise</p>
                <ul className="space-y-3">
                  {item.architecture.prioritise.map((m) => (
                    <li key={m} className="font-serif text-xl md:text-2xl border-b border-paper/15 pb-3">{m}</li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-paper/50 mb-6">We avoid</p>
                <ul className="space-y-3">
                  {item.architecture.avoid.map((m) => (
                    <li key={m} className="font-serif text-xl md:text-2xl text-paper/45 line-through decoration-vermilion/60 border-b border-paper/15 pb-3">{m}</li>
                  ))}
                </ul>
              </div>
            </Reveal>

            {item.architecture.vision && (
              <Reveal>
                <p className="text-xs uppercase tracking-[0.25em] text-vermilion mb-6">Long-term vision</p>
                <p className="text-lg md:text-2xl text-paper/85 leading-relaxed max-w-5xl">
                  {item.architecture.vision}
                </p>
              </Reveal>
            )}

            <Reveal>
              <p className="font-serif text-3xl md:text-5xl lg:text-6xl leading-[1.05] max-w-5xl">
                {item.architecture.finalStatement}
              </p>
            </Reveal>
          </div>
        </section>
      )}

      {/* Gallery */}
      {item.gallery?.length > 0 && (
        <section data-testid="case-gallery" className="px-5 md:px-12 py-20 md:py-28 max-w-[1600px] mx-auto">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-vermilion mb-3">
              {item.galleryLabel || "From the deck"}
            </p>
            <h2 className="font-serif text-3xl md:text-5xl text-ink mb-4">
              {item.galleryNote || "the work, up close."}
            </h2>
            <p className="text-sm text-muted2 mb-12 max-w-xl">
              real pages from the actual project. not polished case-study mockups, the work as it was made.
            </p>
          </Reveal>
          <div className="columns-2 md:columns-3 gap-4 md:gap-6 [column-fill:_balance]">
            {item.gallery.map((src, i) => (
              <Reveal key={src} delay={(i % 3) * 0.06} className="mb-4 md:mb-6 break-inside-avoid">
                <img
                  src={src}
                  alt={`${item.title} direction ${i + 1}`}
                  loading="lazy"
                  className="w-full object-cover bg-ink/5"
                />
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* Next + CTA */}
      <section className="px-5 md:px-12 py-20 md:py-28 max-w-[1600px] mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
        <button
          data-testid="case-next"
          onClick={() => navigate(`/work/${next.slug}`)}
          className="group text-left"
        >
          <span className="text-xs uppercase tracking-[0.25em] text-muted2">Next Project</span>
          <span className="block font-serif text-4xl md:text-6xl text-ink mt-2 group-hover:text-vermilion transition-colors duration-300">
            {next.title} →
          </span>
        </button>
        <a
          data-testid="case-resume"
          href={PROFILE.resumeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 border border-ink px-6 py-3 text-sm uppercase tracking-[0.2em] text-ink hover:bg-ink hover:text-paper transition-colors duration-300 whitespace-nowrap"
        >
          Download Résumé ↗
        </a>
      </section>
      <Footer />
    </main>
  );
}
